import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FrequentlyBought = ({ currentProduct }) => {
  const [selectedItems, setSelectedItems] = useState([]);

  const frequentlyBoughtItems = [
    {
      id: 1,
      name: "Premium Gift Wrapping",
      price: 12.99,
      image: "/assets/images/no_image.png",
      description: "Beautiful wrapping with ribbon and card",
      category: "service",
      selected: false
    },
    {
      id: 2,
      name: "Personalized Gift Card",
      price: 4.99,
      image: "/assets/images/no_image.png",
      description: "Custom message card with your personal note",
      category: "service",
      selected: false
    },
    {
      id: 3,
      name: "Luxury Candle Set",
      price: 34.99,
      originalPrice: 44.99,
      image: "/assets/images/no_image.png",
      description: "Complements any gift with relaxing ambiance",
      category: "product",
      selected: false
    },
    {
      id: 4,
      name: "Artisan Chocolate Box",
      price: 24.99,
      originalPrice: 29.99,
      image: "/assets/images/no_image.png",
      description: "Sweet addition that everyone loves",
      category: "product",
      selected: false
    }
  ];

  const [items, setItems] = useState(frequentlyBoughtItems);

  const toggleItem = (id) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, selected: !item.selected } : item
    ));
  };

  const selectedItemsData = items.filter(item => item.selected);
  const totalPrice = selectedItemsData.reduce((sum, item) => sum + item.price, 0);
  const originalTotal = selectedItemsData.reduce((sum, item) => sum + (item.originalPrice || item.price), 0);
  const savings = originalTotal - totalPrice;

  const addAllToCart = () => {
    console.log('Adding to cart:', selectedItemsData);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-heading text-2xl font-bold text-accent-800 mb-2">
          Frequently Bought Together
        </h2>
        <p className="text-accent-600">
          Complete your gift with these popular add-ons
        </p>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((item) => (
          <div
            key={item.id}
            className={`bg-white rounded-2xl border-2 transition-all duration-300 cursor-pointer hover:shadow-lg ${
              item.selected
                ? 'border-primary shadow-warm'
                : 'border-accent-100 hover:border-accent-200'
            }`}
            onClick={() => toggleItem(item.id)}
          >
            <div className="relative">
              {/* Selection Indicator */}
              <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                item.selected
                  ? 'bg-primary border-primary' :'bg-white border-accent-300'
              }`}>
                {item.selected && (
                  <Icon name="Check" size={14} className="text-white" />
                )}
              </div>

              {/* Image */}
              <div className="bg-accent-50 rounded-t-2xl overflow-hidden" style={{ height: '120px' }}>
                <Image
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-heading font-semibold text-accent-800 mb-1 line-clamp-1">
                  {item.name}
                </h3>
                <p className="text-xs text-accent-600 mb-2 line-clamp-2">
                  {item.description}
                </p>

                {/* Price */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-accent-800">
                      ${item.price}
                    </span>
                    {item.originalPrice && (
                      <span className="text-sm text-accent-500 line-through">
                        ${item.originalPrice}
                      </span>
                    )}
                  </div>
                  {item.originalPrice && (
                    <div className="bg-error text-white px-2 py-1 rounded-full text-xs font-medium">
                      SAVE
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Selection Summary */}
      {selectedItemsData.length > 0 && (
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-accent-800">
              Your Selection
            </h3>
            <button
              onClick={() => setItems(items.map(item => ({ ...item, selected: false })))}
              className="text-sm text-accent-600 hover:text-accent-800 transition-colors duration-200"
            >
              Clear All
            </button>
          </div>

          {/* Selected Items */}
          <div className="space-y-2 mb-4">
            {selectedItemsData.map((item) => (
              <div key={item.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent-100 rounded-lg overflow-hidden">
                    <Image
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-sm font-medium text-accent-800">
                    {item.name}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-bold text-accent-800">
                    ${item.price}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleItem(item.id);
                    }}
                    className="text-accent-500 hover:text-accent-700 transition-colors duration-200"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Total and Actions */}
          <div className="border-t border-accent-200 pt-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-accent-800">
                    Total: ${totalPrice.toFixed(2)}
                  </span>
                  {savings > 0 && (
                    <span className="text-sm font-medium text-success">
                      (Save ${savings.toFixed(2)})
                    </span>
                  )}
                </div>
                <p className="text-sm text-accent-600">
                  {selectedItemsData.length} item{selectedItemsData.length !== 1 ? 's' : ''} selected
                </p>
              </div>
              <Button
                variant="primary"
                iconName="ShoppingCart"
                iconPosition="left"
                onClick={addAllToCart}
                className="shadow-warm"
              >
                Add All to Cart
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Benefits */}
      <div className="bg-white rounded-2xl border border-accent-100 p-6">
        <h3 className="font-heading text-lg font-semibold text-accent-800 mb-4">
          Why Buy Together?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3">
            <Icon name="Truck" size={20} className="text-success mt-1" />
            <div>
              <h4 className="font-medium text-accent-800">One Shipment</h4>
              <p className="text-sm text-accent-600">Everything arrives together</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="DollarSign" size={20} className="text-success mt-1" />
            <div>
              <h4 className="font-medium text-accent-800">Better Value</h4>
              <p className="text-sm text-accent-600">Bundle savings on popular items</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="Heart" size={20} className="text-success mt-1" />
            <div>
              <h4 className="font-medium text-accent-800">Complete Gift</h4>
              <p className="text-sm text-accent-600">Everything they need in one package</p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Insights */}
      <div className="bg-accent-50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-accent-800">
            Customer Insights
          </h3>
          <Icon name="TrendingUp" size={20} className="text-primary" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary mb-1">87%</div>
            <p className="text-sm text-accent-600">bought gift wrapping</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-secondary mb-1">73%</div>
            <p className="text-sm text-accent-600">added a complementary item</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrequentlyBought;